import { WISHLIST_WIDGET_NAMES } from "@/constants/widget-names";
import { modalManagerToken } from "@/tokens";
import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import { productsServiceToken } from "@modules/products/tokens";
import { map } from "rxjs";
import { container, injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import WishlistDrawerWidgetComponent from "./WishlistDrawerWidget.svelte";
import { WishlistDrawerWidgetConfigInterface } from "./WishlistDrawerWidgetConfig";

@injectable()
class WishlistDrawerWidget implements WidgetInterface {
    private productsService: ProductsServiceInterface;
    private dynamicImportManager: DynamicImportManager;
    private wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;

    constructor(
        private readonly config: WishlistDrawerWidgetConfigInterface,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("WishlistDrawerWidget is initialized", config.target);
        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "wishlist.WishlistDrawerWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "wishlist.WishlistDrawerWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "wishlist.critical.WishlistDrawerWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "wishlist.critical.WishlistDrawerWidget"
            )
        );

        this.productsService = container.resolve(productsServiceToken);
        this.dynamicImportManager =
            container.resolve(DynamicImportModule).exports.dynamicImportManager;
        this.wishlistsStorefrontValuesService = container.resolve(
            WishlistsStorefrontValuesService
        );
    }

    async init(): Promise<this> {
        const element = this.config.target;
        this.productsService.subscribe();
        const customizationManager = container.resolve(CustomizationManager);

        let placeholder = element.querySelector<HTMLElement>(
            // TODO: Перенести этот сделектор в config @savchenko-dev
            ".gw-wl-drawer-widget-placeholder"
        );

        if (!placeholder) {
            placeholder = document.createElement("div");
            placeholder.classList.add("gw-wl-drawer-widget-placeholder");
            placeholder.style.display = "block";
            element.appendChild(placeholder);
        }

        const shadowRoot =
            placeholder.shadowRoot ||
            placeholder.attachShadow({ mode: "open" });

        const customizationManagerConfig = {
            widgetName: WISHLIST_WIDGET_NAMES.WISHLIST_DRAWER_WIDGET,
            placeholder,
            shadowRoot,
        };

        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const showControls$ = this.config.attributesObserver
            .observe(placeholder, "gw-show-controls")
            .pipe(map((value) => value === "true"));

        new WishlistDrawerWidgetComponent({
            target: shadowRoot,
            props: {
                modalManager: container.resolve(modalManagerToken),
                dynamicImportManager: this.dynamicImportManager,
                customizationManager,
                widgetName: WISHLIST_WIDGET_NAMES.WISHLIST_DRAWER_WIDGET,
                logger: this.logger,
                wishlistsStorefrontValuesService:
                    this.wishlistsStorefrontValuesService,
                showControls$: showControls$,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
        });

        return this;
    }
}

export default WishlistDrawerWidget;
